import React from 'react';

const AIInnovation = () => {
  return (
    <main className="pt-32 pb-stack-lg max-w-container-max mx-auto px-margin-page">
        {/*  Hero Section  */}
        <section className="mb-stack-lg flex flex-col md:flex-row gap-gutter items-center">
            <div className="flex-1 space-y-6">
                <div
                    className="inline-block px-3 py-1 bg-surface-container-high text-primary border border-primary/20 rounded-full font-bold text-[10px] uppercase tracking-[0.2em]">
                    Research &amp; Development
                </div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-on-background">
                    Architecting the <br /><span className="text-primary italic">Intelligence</span> Layer
                </h1>
                <p className="text-lg text-on-surface-variant max-w-xl font-medium leading-relaxed">
                    Engineering private, high-performance AI ecosystems. From local LLM orchestration to custom-built
                    hardware servers, I specialize in moving innovation from the cloud to the edge.
                </p>
            </div>
            <div
                className="flex-1 w-full aspect-square md:aspect-video rounded-xl overflow-hidden glass-panel ethereal-glow p-1">
                <div className="w-full h-full rounded-lg overflow-hidden">
                    <img
                        alt="AI and edge computing hardware visualization"
                        className="w-full h-full object-cover opacity-80 mix-blend-lighten hover:opacity-100 transition-all duration-700"
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuApdymMP-6XEkM_-mb6_tpm2g4m6AEbWMfkYIlEk2RajY_8aLBiSHGDkcy3w6UGIVyuDUBXWjtgklVESxp83YK-G1zeVTHrSRee-a6bkcGg5DxnFHZK6iag5JRVNbHxzsYYDKyXJs1Yg-HyfiBiBpUcrNzpSDSu6ipqg4nrJzOMFCsSXzYpVVLwYnUGkHwINDegIrK4MhQQA1dYuZy00pQPE7eRdGr0qDiZS3W0rEsWEp3M5U4y24GfUcTESb9zDfAd6XoPS7NA1I0" />
                </div>
            </div>
        </section>
        {/*  AI/LLM Experience Section  */}
        <section className="mb-stack-lg">
            <div className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-2">Specialization</h2>
                    <h3 className="text-3xl md:text-4xl font-bold tracking-tight">AI &amp; LLM Orchestration</h3>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-primary/50 to-transparent mx-8 hidden md:block"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/*  Local LLM Orchestration  */}
                <div className="md:col-span-8 glass-panel-heavy rounded-2xl p-8 lg:p-10 ethereal-glow">
                    <div className="flex items-center justify-between mb-10">
                        <div
                            className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                            <span className="material-symbols-outlined text-primary text-2xl"
                                data-icon="neurology">neurology</span>
                        </div>
                        <span
                            className="font-mono text-[10px] font-bold text-on-surface-variant bg-surface-container rounded px-2 py-1">v4.2.0-STABLE</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold mb-4">Local LLM Implementation with Ollama</h3>
                    <p className="text-on-surface-variant mb-8 leading-relaxed max-w-2xl">
                        Deploying sovereign AI environments to eliminate cloud latency and data privacy risks. Expertise
                        in quantizing models for specific hardware constraints while maintaining conversational
                        fidelity.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <span
                            className="px-4 py-1.5 glass-panel rounded-full text-primary font-bold text-[10px] uppercase tracking-wider">OLLAMA</span>
                        <span
                            className="px-4 py-1.5 glass-panel rounded-full text-secondary font-bold text-[10px] uppercase tracking-wider">LLAMA
                            3.1</span>
                        <span
                            className="px-4 py-1.5 glass-panel rounded-full text-tertiary font-bold text-[10px] uppercase tracking-wider">MISTRAL</span>
                        <span
                            className="px-4 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full font-bold text-[10px] uppercase tracking-wider">RAG
                            PIPELINES</span>
                    </div>
                    <div className="mt-12 pt-8 border-t border-outline-variant/20 flex items-center gap-4">
                        <div className="flex -space-x-2">
                            <div
                                className="w-10 h-10 rounded-full glass-panel flex items-center justify-center border border-secondary/30">
                                <span className="material-symbols-outlined text-secondary text-sm"
                                    data-icon="memory">memory</span>
                            </div>
                            <div
                                className="w-10 h-10 rounded-full glass-panel flex items-center justify-center border border-primary/30">
                                <span className="material-symbols-outlined text-primary text-sm"
                                    data-icon="bolt">bolt</span>
                            </div>
                        </div>
                        <span className="text-xs font-semibold text-on-surface-variant tracking-wider">ACTIVE RESEARCH:
                            MULTI-MODAL REASONING ENGINES</span>
                    </div>
                </div>
                {/*  Chatbot Integration  */}
                <div
                    className="md:col-span-4 bg-primary-container/20 glass-panel rounded-2xl p-8 border-primary/10 relative overflow-hidden flex flex-col">
                    <div className="absolute -right-10 -top-10 w-32 h-32 bg-primary/5 blur-3xl rounded-full"></div>
                    <div
                        className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20 mb-8">
                        <span className="material-symbols-outlined text-primary text-2xl"
                            data-icon="chat_bubble">chat_bubble</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Semantic Chat Systems</h3>
                    <p className="text-sm text-on-surface-variant mb-auto font-medium leading-relaxed">
                        Building intelligent agents that bridge the gap between structured data and natural language.
                        Implementing prompt engineering patterns like Chain-of-Thought (CoT) and ReAct.
                    </p>
                    <ul className="space-y-4 mt-8">
                        <li className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-primary">
                            <span
                                className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(125,211,252,1)]"></span>
                            Vector Databases
                        </li>
                        <li className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-primary">
                            <span
                                className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(125,211,252,1)]"></span>
                            Context Optimization
                        </li>
                    </ul>
                </div>
            </div>
        </section>
        {/*  Hardware Innovation Section  */}
        <section className="mb-stack-lg">
            <div className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-secondary mb-2">Systems</h2>
                    <h3 className="text-3xl md:text-4xl font-bold tracking-tight">Hardware Infrastructure</h3>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-secondary/50 to-transparent mx-8 hidden md:block"></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div
                    className="glass-panel-heavy rounded-2xl p-8 lg:p-10 relative group hover:border-secondary/30 transition-all">
                    <div className="flex items-start justify-between mb-8">
                        <div>
                            <h4 className="font-bold text-[10px] text-secondary uppercase tracking-[0.25em] mb-1">Server
                                Setup</h4>
                            <h3 className="text-2xl font-bold">Raspberry Pi 5 Cluster</h3>
                        </div>
                        <div
                            className="bg-secondary/10 text-secondary border border-secondary/20 px-3 py-1 rounded-full font-bold text-[10px]">
                            DIETPI OS</div>
                    </div>
                    <p className="text-on-surface-variant mb-10 font-medium leading-relaxed">
                        Optimized low-power compute clusters for 24/7 background tasks and internal tool hosting.
                        Leveraged DietPi for minimal overhead and maximum stability.
                    </p>
                    <div className="space-y-3">
                        <div
                            className="flex justify-between items-center p-4 bg-surface-container/50 border border-outline-variant/10 rounded-xl">
                            <span className="font-semibold text-xs tracking-wider uppercase text-on-surface/80">Memory
                                Optimization</span>
                            <span
                                className="text-[10px] font-bold bg-secondary/10 text-secondary px-2 py-0.5 rounded-full border border-secondary/20">ZRAM
                                ENABLED</span>
                        </div>
                        <div
                            className="flex justify-between items-center p-4 bg-surface-container/50 border border-outline-variant/10 rounded-xl">
                            <span className="font-semibold text-xs tracking-wider uppercase text-on-surface/80">Storage
                                Interface</span>
                            <span
                                className="text-[10px] font-bold bg-secondary/10 text-secondary px-2 py-0.5 rounded-full border border-secondary/20">NVME
                                BOOT</span>
                        </div>
                    </div>
                </div>
                <div
                    className="glass-panel rounded-2xl p-8 lg:p-10 relative border-tertiary/10 hover:border-tertiary/30 transition-all">
                    <div className="flex items-start justify-between mb-8">
                        <div>
                            <h4 className="font-bold text-[10px] text-tertiary uppercase tracking-[0.25em] mb-1">Compute
                                Core</h4>
                            <h3 className="text-2xl font-bold">NVIDIA Edge Model Hosting</h3>
                        </div>
                        <div
                            className="bg-tertiary/10 text-tertiary border border-tertiary/20 px-3 py-1 rounded-full font-bold text-[10px]">
                            CUDA ACCELERATED</div>
                    </div>
                    <p className="text-on-surface-variant mb-10 font-medium leading-relaxed">
                        Hosting localized model APIs for computer vision and NLP tasks using dedicated NVIDIA hardware.
                        Custom Dockerized environments for portable model serving.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-6 rounded-xl glass-panel-heavy flex flex-col gap-4 border-outline-variant/10">
                            <span className="material-symbols-outlined text-tertiary text-2xl"
                                data-icon="speed">speed</span>
                            <span className="font-bold uppercase text-[10px] tracking-widest text-on-surface/60">Sub-100ms
                                Inference</span>
                        </div>
                        <div className="p-6 rounded-xl glass-panel-heavy flex flex-col gap-4 border-outline-variant/10">
                            <span className="material-symbols-outlined text-tertiary text-2xl"
                                data-icon="security">security</span>
                            <span className="font-bold uppercase text-[10px] tracking-widest text-on-surface/60">Local Data
                                Sourcing</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        {/*  Future Focus Section  */}
        <section className="glass-panel-heavy rounded-3xl p-10 md:p-16 relative overflow-hidden ethereal-glow">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-primary/5 blur-[100px] rounded-full"></div>
            <div className="absolute -left-20 -bottom-20 w-60 h-60 bg-tertiary/5 blur-[80px] rounded-full"></div>
            <div className="max-w-4xl relative">
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-8 leading-tight">
                    Future Focus: <br /><span className="text-primary italic">AI-Driven Architectures</span>
                </h2>
                <p className="text-lg font-medium text-on-surface-variant mb-16 leading-relaxed max-w-2xl">
                    My vision for the next generation of mobile and web architectures centers on <span
                        className="text-on-surface px-1 bg-primary/10 rounded border-b border-primary/30">Native
                        Intelligence</span>. This means moving beyond simple API calls to integrating deep learning
                    models directly into the client-side lifecycle.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pt-12 border-t border-outline-variant/10">
                    <div className="space-y-4">
                        <h4 className="text-sm font-bold text-primary uppercase tracking-[0.2em] italic">01. Predictive UX
                        </h4>
                        <p
                            className="text-xs font-semibold text-on-surface-variant uppercase leading-relaxed tracking-wider">
                            Interfaces that anticipate user intent using lightweight on-device behavior analysis.</p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-sm font-bold text-primary uppercase tracking-[0.2em] italic">02. Autonomous Code
                        </h4>
                        <p
                            className="text-xs font-semibold text-on-surface-variant uppercase leading-relaxed tracking-wider">
                            Self-healing frontend architectures and automated unit test generation integrated into
                            CI/CD.</p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-sm font-bold text-primary uppercase tracking-[0.2em] italic">03. Edge Privacy
                        </h4>
                        <p
                            className="text-xs font-semibold text-on-surface-variant uppercase leading-relaxed tracking-wider">
                            Full end-to-end encryption where the model remains local, ensuring absolute data
                            sovereignty.</p>
                    </div>
                </div>
            </div>
        </section>
    </main>
  );
};

export default AIInnovation;
